import api from '../../services/api';
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PageContainer from '../../common/components/PageContainer';
import useAlerta from '../../common/components/useAlerta';
import AppSnackbar from '../../common/components/AppSnackbar';

function Romaneio() {
    const { register, handleSubmit, reset, getValues } = useForm();
    const { alerta, mostrarAlerta, fecharAlerta } = useAlerta();
    const theme = useTheme();
    const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
    const [destinatario, setDestinatario] = useState([]);
    const [destinatarios, setDestinatarios] = useState([]);
    const [listaDeItens, setListaDeItens] = useState([]);
    const [novoItem, setNovoItem] = useState({
        descricao: "",
        quantidade: "",
        volume: "",
    });
    const [showNewDestinatario, setShowNewDestinatario] = useState(false);

    const adicionarItem = () => {
        if (!novoItem.descricao || !novoItem.quantidade || !novoItem.foto) {
            mostrarAlerta("Descrição, Quantidade e Foto são obrigatórias!", "error");
            return;
        }

        setListaDeItens((prevItens) => [...prevItens, novoItem]);
        setNovoItem({ descricao: "", quantidade: "", volume: "", foto: "" });
        mostrarAlerta("Item adicionado à carga!", "success");
        setTimeout(fecharAlerta, 3000);
    };

    const removerItem = (index) => {
        setListaDeItens((prevItens) => prevItens.filter((_, i) => i !== index));
    };

    const handleNovoDestinatario = async () => {
        try {
            const novoDest = getValues("novoDestinatario");
            await api.post("/romaneio/destinatario", novoDest);
            fetchDestinatarios();
            setShowNewDestinatario(false);
            mostrarAlerta("Destinatário cadastrado com sucesso!", "success");
            reset();
        } catch (error) {
            console.error(error);
            mostrarAlerta("Erro ao cadastrar destinatário!", "error");
        }
    };

    const fetchDestinatarios = async () => {
        try {
            const response = await api.get("/romaneio/destinatario");
            if (Array.isArray(response.data)) {
                setDestinatarios(response.data);
            } else {
                console.error("A API não retornou um array como esperado.");
                setDestinatarios([]);
            }
        } catch (error) {
            console.error("Erro ao carregar destinatários:", error);
            setDestinatarios([]);
        }
    };

    useEffect(() => {
        fetchDestinatarios();
    }, []);

    const onSubmit = async (data) => {
        if (listaDeItens.length === 0) {
            mostrarAlerta("Adicione pelo menos um item à carga!", "error");
            return;
        }

        const combinedDateTime = `${data.dataDate}T${data.dataTime}`;
        const formData = {
            ...data,
            idDestinatario: destinatario,
            carga: listaDeItens,
            data: combinedDateTime,
        };

        try {
            const response = await api.post("/romaneio", formData, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            reset();
            setListaDeItens([]);
            window.open(url, '_blank');
            mostrarAlerta("Romaneio cadastrado com sucesso!", "success");
        } catch (error) {
            console.error(error);
            mostrarAlerta("Erro ao cadastrar romaneio!", "error");
        }
    };

    return (
        <PageContainer sx={{ maxWidth: 900 }}>
            <Paper component="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: { xs: 2, sm: 4 } }}>
                <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
                    Romaneio
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField {...register("natOperacao")} label="Natureza da Operação" placeholder="Ex.: Venda, Transferência" required fullWidth />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField {...register("dataDate")} label="Data" type="date" InputLabelProps={{ shrink: true }} required fullWidth />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField {...register("dataTime")} label="Hora" type="time" InputLabelProps={{ shrink: true }} required fullWidth />
                    </Grid>

                    <Grid item xs={12} sm={9}>
                        <TextField
                            {...register("destinatario")}
                            select
                            label="Destinatário"
                            defaultValue=""
                            onChange={(e) => setDestinatario(e.target.value)}
                            required
                            fullWidth
                        >
                            <MenuItem value="">Selecione um destinatário</MenuItem>
                            {(Array.isArray(destinatarios) ? destinatarios : []).map((dest) => (
                                <MenuItem key={dest.idDestinatario} value={dest.idDestinatario}>
                                    {dest.nome + " | " + dest.rua + ", " + dest.numero + ", " + dest.bairro + ", " + dest.cidade + " - " + dest.estado}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Button variant="outlined" fullWidth sx={{ height: '100%' }} onClick={() => setShowNewDestinatario(true)}>
                            Novo destinatário
                        </Button>
                    </Grid>
                </Grid>

                <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                    Adicionar Item
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Descrição"
                            placeholder="Descrição do item"
                            value={novoItem.descricao || ""}
                            onChange={(e) => setNovoItem({ ...novoItem, descricao: e.target.value })}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField
                            label="Quantidade"
                            type="number"
                            value={novoItem.quantidade || ""}
                            onChange={(e) => setNovoItem({ ...novoItem, quantidade: e.target.value })}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField
                            label="Volume"
                            value={novoItem.volume || ""}
                            onChange={(e) => setNovoItem({ ...novoItem, volume: e.target.value })}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={8} sm={3}>
                        <Button variant="outlined" component="label" fullWidth>
                            {novoItem.foto ? 'Foto selecionada' : 'Selecionar foto'}
                            <input
                                type="file"
                                accept="image/*"
                                capture
                                hidden
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        const reader = new FileReader();
                                        reader.onload = () => setNovoItem({ ...novoItem, foto: reader.result });
                                        reader.readAsDataURL(file);
                                    }
                                }}
                            />
                        </Button>
                    </Grid>
                    <Grid item xs={4} sm={1}>
                        <Button variant="contained" color="secondary" fullWidth sx={{ height: '100%' }} onClick={adicionarItem}>
                            +
                        </Button>
                    </Grid>
                </Grid>

                <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                    Itens da Carga
                </Typography>
                {listaDeItens.length === 0 ? (
                    <Typography color="text.secondary">Nenhum item adicionado ainda.</Typography>
                ) : (
                    <Grid container spacing={2}>
                        {listaDeItens.map((item, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Card
                                    sx={{
                                        height: 160,
                                        backgroundImage: `url(${item.foto})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        display: 'flex',
                                        alignItems: 'flex-end',
                                        position: 'relative',
                                    }}
                                >
                                    <Box sx={{ bgcolor: 'rgba(10, 26, 43, 0.72)', color: '#fff', p: 1.5, width: '100%' }}>
                                        <Typography variant="body2">Descrição: {item.descricao}</Typography>
                                        <Typography variant="body2">Quantidade: {item.quantidade}</Typography>
                                        <Typography variant="body2">Volume: {item.volume}</Typography>
                                        <Button
                                            size="small"
                                            color="error"
                                            startIcon={<DeleteOutlineIcon />}
                                            onClick={() => removerItem(index)}
                                            sx={{ mt: 0.5, color: '#fff' }}
                                        >
                                            Remover
                                        </Button>
                                    </Box>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Button type="submit" variant="contained" color="secondary" size="large" sx={{ px: 6 }}>
                        Gerar romaneio
                    </Button>
                </Box>
            </Paper>

            <Dialog
                open={showNewDestinatario}
                onClose={() => setShowNewDestinatario(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={fullScreenDialog}
            >
                <DialogTitle>Novo Destinatário</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField {...register("novoDestinatario.nome")} label="Nome" fullWidth />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField {...register("novoDestinatario.cpf")} label="CPF" fullWidth />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField {...register("novoDestinatario.cnpj")} label="CNPJ" fullWidth />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <TextField {...register("novoDestinatario.rua")} label="Rua" fullWidth />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField {...register("novoDestinatario.numero")} label="Número" fullWidth />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField {...register("novoDestinatario.bairro")} label="Bairro" fullWidth />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField {...register("novoDestinatario.cidade")} label="Cidade" fullWidth />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <TextField {...register("novoDestinatario.estado")} label="Estado (Ex: PB)" fullWidth />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <TextField {...register("novoDestinatario.cep")} label="CEP" fullWidth />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField {...register("novoDestinatario.telefone")} label="Telefone" fullWidth />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowNewDestinatario(false)} color="inherit">Cancelar</Button>
                    <Button onClick={handleNovoDestinatario} variant="contained" color="secondary">Salvar</Button>
                </DialogActions>
            </Dialog>

            <AppSnackbar alerta={alerta} onClose={fecharAlerta} />
        </PageContainer>
    );
}

export default Romaneio;
